import { inject, Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthCustomService } from '@src/app/core/services/auth-custom/auth-custom.service';
import { HeadlessService } from '@src/app/core/services/headless/headless.service';
import { LoadingService } from '@src/app/core/services/loading/loading.service';
import { LogoService } from '@src/app/core/services/logo/logo.service';
import { TipoTercero } from '@src/app/core/enum/tipo-tercero.enum';
import { User } from '@src/app/core/types/user.type';
import { attachLogosToCompanies } from '@src/app/core/utils/logo.utils';
import { isProfilingEnabled } from '@src/app/core/utils/feature-flags.util';
import { KAM_REQUIRED_ROUTES } from '@src/app/pages/services/utils/consts';
import { from, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AuthActions } from './auth.actions';
import { AuthSelectors } from './auth.selectors';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private headlessService = inject(HeadlessService);
  private loadingService = inject(LoadingService);
  private logoService = inject(LogoService);
  private authCustomService = inject(AuthCustomService);
  private store = inject(Store);
  private router = inject(Router);
  private location = inject(Location);
  private loadingKamCompanyId: string | null = null;

  loadUserInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadUserInfo),
      withLatestFrom(this.store.select(AuthSelectors.selectCompaniesParams)),
      switchMap(([action, params]) => {
        return this.headlessService.getCurrentUsuario(params).pipe(
          switchMap((currentUser) => {
            currentUser.CLEAN_NOMBRE_DS = cleanUserName(currentUser);
            return of(AuthActions.loadUserInfoSuccess({ user: currentUser }));
          }),
          catchError((error) => {
            console.error('Error loading current user:', error);
            return of(AuthActions.loadUserInfoFailure({ error }));
          }),
        );
      }),
    ),
  );

  loadCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadCompanies),
      withLatestFrom(this.store.select(AuthSelectors.selectCompaniesParams)),
      switchMap(([action, params]) => {
        return this.headlessService.getCurrentUsuario(params).pipe(
          map((currentUser) => {
            return AuthActions.loadCompaniesSuccess({
              companies: currentUser.EMPRESAS,
              pagination: currentUser.PAGINATION,
            });
          }),
          catchError((error) => {
            console.error('Error loading current user:', error);
            return of(AuthActions.loadCompaniesFailure({ error }));
          }),
        );
      }),
    ),
  );

  private getCompanyActions(companies: any[]) {
    if (companies.length === 0) {
      return [AuthActions.loadCompaniesSuccess({ companies })];
    }

    const localStorageValue = localStorage.getItem('portalSelectedCompany');
    const selectedCompany = localStorageValue
      ? companies.find((company) => company.CLIEN_NM === localStorageValue) ||
        companies[0]
      : companies[0];

    return [
      AuthActions.setSelectedCompany({ company: selectedCompany }),
      AuthActions.loadCompaniesSuccess({ companies }),
      AuthActions.loadCompanyLogos(),
    ];
  }

  loadCompaniesFromUserInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadUserInfoSuccess),
      withLatestFrom(this.store.select(AuthSelectors.selectCompanies)),
      switchMap(([action, companies]) => this.getCompanyActions(companies)),
    ),
  );

  setSelectedCompanyStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.setSelectedCompany),
      switchMap(({ company }) => {
        if (company?.CLIEN_NM) {
          localStorage.setItem('portalSelectedCompany', company.CLIEN_NM);
          return of(AuthActions.changeCompanySuccess({ company }));
        }
        return of(
          AuthActions.changeCompanyFailure({ error: 'No company provided' }),
        );
      }),
    ),
  );

  loadKam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadKam),
      withLatestFrom(this.store.select(AuthSelectors.selectSelectedCompany)),
      filter(([_, company]) => {
        if (!company?.CLIEN_NM) return false;
        if (this.loadingKamCompanyId === company.CLIEN_NM) return false;
        return true;
      }),
      tap(([_, company]) => {
        this.loadingKamCompanyId = company!.CLIEN_NM;
      }),
      switchMap(() => {
        return this.headlessService.getKam().pipe(
          map((kam) => AuthActions.loadKamSuccess({ kam })),
          catchError(() => of(AuthActions.loadKamFailure())),
          finalize(() => {
            this.loadingKamCompanyId = null;
          }),
        );
      }),
    ),
  );

  loadActiveContracts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadActiveContracts),
      switchMap(() =>
        this.headlessService.getContratosActivos().pipe(
          map((contracts) =>
            AuthActions.loadActiveContractsSuccess({ contracts }),
          ),
          catchError(() => of(AuthActions.loadActiveContractsFailure())),
        ),
      ),
    ),
  );

  loadCompanyLogos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadCompaniesSuccess, AuthActions.loadCompanyLogos),
      withLatestFrom(
        this.store.select(AuthSelectors.selectCompanies),
        this.store.select(AuthSelectors.selectSelectedCompany),
      ),
      switchMap(([action, companies, selectedCompany]) => {
        if (!companies.length) {
          return of(AuthActions.loadCompanyLogosSuccess({ companies }));
        }

        const companyId = selectedCompany?.CLIEN_NM;

        return this.logoService.getLogos(companyId).pipe(
          map((logoData) => {
            const companiesWithLogos = attachLogosToCompanies(
              companies,
              logoData,
            );
            return AuthActions.loadCompanyLogosSuccess({
              companies: companiesWithLogos,
            });
          }),
          catchError((error) => {
            console.error('Error loading company logos:', error);
            return of(AuthActions.loadCompanyLogosFailure({ error }));
          }),
        );
      }),
    ),
  );

  updateSelectedCompanyWithLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadCompanyLogosSuccess),
      withLatestFrom(this.store.select(AuthSelectors.selectSelectedCompany)),
      map(([{ companies }, selectedCompany]) => {
        if (selectedCompany && companies) {
          const updatedSelectedCompany = companies.find(
            (c) => c.CLIEN_NM === selectedCompany.CLIEN_NM,
          );

          if (
            updatedSelectedCompany &&
            updatedSelectedCompany.logoUrl !== selectedCompany.logoUrl
          ) {
            return AuthActions.setSelectedCompany({
              company: updatedSelectedCompany,
            });
          }
        }
        return { type: 'NO_UPDATE_NEEDED' };
      }),
    ),
  );

  loadDeclaracionApiToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AuthActions.loadDeclaracionApiToken,
        AuthActions.refreshDeclaracionApiToken,
      ),
      switchMap(() =>
        from(this.authCustomService.getTokenForEcoembesApi()).pipe(
          map((response) => {
            if (response?.access_token) {
              return AuthActions.loadDeclaracionApiTokenSuccess({
                token: response.access_token,
              });
            }
            return AuthActions.loadDeclaracionApiTokenFailure({
              error: 'No token received',
            });
          }),
          catchError((error) => {
            console.error('Error obtaining token for Ecoembes API:', error);
            return of(AuthActions.loadDeclaracionApiTokenFailure({ error }));
          }),
        ),
      ),
    ),
  );

  loadCompanyCIF$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadCompanyCIF),
      withLatestFrom(this.store.select(AuthSelectors.selectSelectedCompany)),
      filter(([_, company]) => !company?.CIF_NIF),
      switchMap(() =>
        this.headlessService.getDatosEmpresas().pipe(
          map((companyDetails) => {
            const cif = companyDetails?.CIF || '';
            return AuthActions.loadCompanyCIFSuccess({ cif });
          }),
          catchError((error) => {
            console.error('Error loading company CIF:', error);
            return of(AuthActions.loadCompanyCIFFailure({ error }));
          }),
        ),
      ),
    ),
  );

  redirectPoseedorFinalIntegralToForbidden$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.changeCompanySuccess),
        filter(
          ({ company }) =>
            company.TIPO_TERCERO === TipoTercero.POSEEDOR_FINAL_INTEGRAL &&
            !isProfilingEnabled(),
        ),
        tap(() => {
          this.router.navigate(['/forbidden']);
        }),
      ),
    { dispatch: false },
  );

  validateKamAccessOnCompanyChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.setSelectedCompany),
        tap(() => {
          this.store.dispatch(AuthActions.loadKam());
          this.store.dispatch(AuthActions.loadActiveContracts());
          this.store.dispatch(AuthActions.loadCompanyLogos()); // 👈 recarga logo al cambiar empresa
        }),
        // distinctUntilChanged DESPUÉS del tap: loadKam/loadActiveContracts/loadCompanyLogos
        // se despachan siempre, pero el refresh trick solo se ejecuta cuando la empresa
        // CAMBIA realmente (no cuando la misma empresa se vuelve a despachar en un loop).
        distinctUntilChanged(
          (a, b) => a.company?.CLIEN_NM === b.company?.CLIEN_NM,
        ),
        switchMap(() =>
          this.actions$.pipe(
            ofType(AuthActions.loadKamSuccess, AuthActions.loadKamFailure),
            take(1),
            withLatestFrom(
              this.store.select(AuthSelectors.selectHasKam),
              this.store.select(AuthSelectors.canAccessPortal),
            ),
            tap(([, hasKam, canAccessPortal]) => {
              // this.router.url aún no está comprometido en la primera navegación
              // (mientras un guard sigue pendiente de esta misma acción); location.path()
              // refleja la URL real del navegador en todo momento.
              const currentUrl = this.location.path(true) || '/';
              const requiresKam = KAM_REQUIRED_ROUTES.some((route) =>
                currentUrl.includes(route),
              );

              if (requiresKam && !hasKam) {
                this.router.navigate(['/services']);
              } else if (canAccessPortal && !currentUrl.includes('forbidden')) {
                // No ejecutar el refresh trick cuando la URL actual es /forbidden:
                // el router aún puede estar procesando la navegación hacia /forbidden
                // y la navegación a '/' desencadenaría el authGuard (activando LayoutComponent)
                // antes de que ngOnInit de ForbiddenComponent ejecute window.location.assign.
                const navigateToUrl =
                  currentUrl === '/home' || currentUrl === '/' ? '/help' : '/';

                this.router
                  .navigateByUrl(navigateToUrl, { skipLocationChange: true })
                  .then(() => {
                    this.router.navigateByUrl(currentUrl);
                  });
              }
            }),
          ),
        ),
      ),
    { dispatch: false },
  );
}

function cleanUserName(user: User) {
  if (!user?.NOMBRE_DS_CRM) return '';
  return user.NOMBRE_DS_CRM.replace(/\s*\(.*?\)\s*/g, '').trim();
}

export { cleanUserName };
